/**
 * Display helpers for WasteSense sites. Centralised so the priority
 * map list, the incident-logs section, and the map popups all show
 * sites the same way and sort them in the same order. Adding a
 * fourth surface? Route it through here too.
 *
 * The upstream pipeline emits titles as `"<address> — <suburb> — <state>"`,
 * but operators read them by suburb first ("everything Glenroy
 * clustered together, then Brunswick…"), so we flip the order on
 * display and sort by suburb.
 */
import type { WasteSenseSite, WasteSenseMapSite } from "./types";

/**
 * Splits a site title like `"331 Barkly St — Brunswick — VIC"` into
 * its `{ address, suburb }` parts. Tolerant of titles that lack a
 * suburb segment (e.g. older fixtures or test data) — falls back to
 * the whole title as the address with `suburb = ""`.
 */
export interface SiteHeading {
  /** Display string: `"<suburb> — <address>"` (or just `<address>` when no suburb). */
  primary: string;
  /** Lowercased suburb, for stable case-insensitive sorting. */
  suburbKey: string;
  /** Lowercased address, used as the secondary sort key. */
  addressKey: string;
}

const TITLE_DELIMITER = " — ";

export function parseSiteTitle(title: string): SiteHeading {
  const parts = title.split(TITLE_DELIMITER).map((s) => s.trim());
  const address = parts[0] ?? title;
  const suburb = parts[1] ?? "";

  /**
   * State (parts[2]) is intentionally dropped from the display.
   * It's redundant inside a single-tenant report (everything's
   * VIC for Merri-bek, NSW for Midcoast, etc.) and adds visual
   * noise to dense list rows.
   */
  const primary = suburb ? `${suburb}${TITLE_DELIMITER}${address}` : address;

  return {
    primary,
    suburbKey: suburb.toLowerCase(),
    addressKey: address.toLowerCase(),
  };
}

/** Convenience wrapper — returns just the display string. */
export function formatSiteHeading(title: string): string {
  return parseSiteTitle(title).primary;
}

/**
 * Stable site comparator: by suburb (case-insensitive), then by
 * address (case-insensitive). Sites with no suburb sort after sites
 * that have one — they'd otherwise float to the top under empty-string
 * comparison and obscure the suburb groupings.
 */
export function compareSites(a: WasteSenseSite, b: WasteSenseSite): number {
  const ah = parseSiteTitle(a.title);
  const bh = parseSiteTitle(b.title);
  if (ah.suburbKey && !bh.suburbKey) return -1;
  if (!ah.suburbKey && bh.suburbKey) return 1;
  if (ah.suburbKey !== bh.suburbKey) return ah.suburbKey.localeCompare(bh.suburbKey);
  return ah.addressKey.localeCompare(bh.addressKey);
}

/**
 * Returns the input array sorted by `compareSites`. Non-mutating —
 * the caller's array isn't touched, so React's referential checks
 * stay sound for memoised consumers.
 */
export function sortSites(sites: readonly WasteSenseSite[]): readonly WasteSenseSite[] {
  return [...sites].sort(compareSites);
}

/**
 * Same heading formatter for the map's popup, which carries
 * `site_title` rather than `title`. Keeping a separate entry point
 * makes the call sites read cleanly without forcing them to
 * remember which field name applies.
 */
export function formatMapSiteHeading(site: Pick<WasteSenseMapSite, "site_title">): string {
  return formatSiteHeading(site.site_title);
}
